import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, ArrowRight, Clock, Check } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import HeroSlideshow from "@/components/HeroSlideshow";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { SERVICES, CONTACT, PARTNERS } from "@/data/site";

const HERO_LINES = [
  { text: "Décrivez votre", accent: false },
  { text: "événement.", accent: false },
  { text: "Nous l'organisons", accent: true },
  { text: "de A à Z.", accent: true },
];

const HERO_SLIDES = [
  {
    kind: "video",
    src: "/videos/hero-lieu.mp4",
    webm: "/videos/hero-lieu.webm",
    poster: "/images/poster-lieu.webp",
    label: "Lieux d'exception",
    alt: "Lieu de réception orné — architecture grandiose et mise en lumière",
  },
  {
    kind: "video",
    src: "/videos/hero-toast.mp4",
    webm: "/videos/hero-toast.webm",
    poster: "/images/poster-toast.webp",
    label: "Réceptions & toasts",
    alt: "Toast au champagne lors d'une réception élégante",
  },
  {
    kind: "video",
    src: "/videos/hero-gala.mp4",
    webm: "/videos/hero-gala.webm",
    poster: "/images/poster-gala.webp",
    label: "Dîners de gala",
    alt: "Table de dîner de gala dressée avec candélabres",
  },
];

const DIFFERENCE_CARDS = [
  {
    num: "01",
    title: "Un interlocuteur unique",
    text: "Un chef de projet dédié du premier échange au jour J. Vous ne courez pas après dix prestataires : vous parlez à une seule personne, qui répond.",
  },
  {
    num: "02",
    title: "Tout est coordonné, de A à Z",
    text: "Organisation, coordination, traiteur, bar mobile, personnel, hôtesses, animation, mobilier, technique, logistique : nous pilotons chaque poste.",
  },
  {
    num: "03",
    title: "Des partenaires spécialisés",
    text: "Lieux, traiteurs, techniciens et artistes sélectionnés et éprouvés. Nous mobilisons les bons experts pour votre concept, votre budget et vos exigences.",
  },
];

const OFFERS = [
  {
    title: "Inauguration dans vos locaux",
    price: "dès 5 000 € HT",
    capacity: "Jusqu'à 150 invités · 1/2 journée",
    includes: ["Scénographie du moment officiel (ruban, discours)", "Sonorisation et pupitre", "Cocktail traiteur et boissons", "Hôtesses d'accueil", "Coordination complète le jour J"],
    options: "Options : barnum, presse locale, photographe",
    conditions: "Installation la veille ou le matin même, démontage inclus",
    to: "/inauguration-entreprise-lyon",
    cta: "Organiser mon inauguration",
  },
  {
    title: "Afterwork cocktail & bar mobile",
    price: "dès 3 500 € HT",
    capacity: "50 à 120 personnes · 18h – 22h",
    includes: ["Bar mobile et bartenders (avec et sans alcool)", "Cocktail traiteur et finger food", "Musique et sono adaptée", "Mobilier lounge", "Coordination et démontage"],
    options: "Options : photobooth, magicien close-up, bar à jus premium",
    conditions: "Dans vos locaux ou lieu privatisé — installation en 2 h",
    to: "/afterwork-entreprise-lyon",
    cta: "Imaginer mon afterwork",
  },
  {
    title: "Soirée de Noël d'entreprise",
    price: "dès 18 000 € HT",
    capacity: "80 à 200 personnes · soirée complète",
    includes: ["Lieu privatif ou vos locaux", "Restauration festive complète", "Scénographie de Noël", "DJ et animations", "Coordination complète le jour J"],
    options: "Options : Père Noël, cadeaux enfants (arbre de Noël), photographe",
    conditions: "Repérage technique inclus — réserver dès septembre conseillé",
    to: "/fete-de-noel-entreprise/lyon",
    cta: "Préparer ma soirée de Noël",
  },
];

const STEPS = [
  { num: "01", title: "Vous déposez votre brief", text: "Deux minutes suffisent : type d'événement, ville, date, nombre de convives, budget estimatif." },
  { num: "02", title: "Vous recevez 3 scénarios", text: "Fourchette indicative immédiate, échange de cadrage sous 24 h ouvrées, puis trois scénarios chiffrés — Essentiel, Signature, Prestige." },
  { num: "03", title: "Nous préparons tout", text: "Lieux, traiteur, technique, personnel, animation : votre chef de projet coordonne chaque prestataire." },
  { num: "04", title: "Vous vivez le jour J", text: "Votre interlocuteur unique supervise l'installation et le déroulé. Vous, vous accueillez vos invités." },
];

const FAQ = [
  {
    q: "En combien de temps recevons-nous une réponse ?",
    a: "Première réponse sous 24 h ouvrées. Nous étudions votre brief, puis nous vous rappelons pour cadrer votre projet avant de le chiffrer.",
  },
  {
    q: "Pouvez-vous gérer la totalité de l'organisation ?",
    a: "Oui, de A à Z : recherche de lieu, traiteur, bar mobile, technique, personnel et hôtesses, animation, mobilier et logistique. Vous gardez un interlocuteur unique pour l'ensemble.",
  },
  {
    q: "Intervenez-vous en dehors de Lyon ?",
    a: "Oui. Nous organisons des événements d'entreprise à Lyon, à Paris et partout en France, ainsi que des voyages d'entreprise à l'étranger.",
  },
  {
    q: "Quel budget prévoir pour un événement d'entreprise ?",
    a: "Tout dépend du format, du lieu et du nombre de convives. Déposez votre brief : vous recevez une proposition chiffrée et détaillée, poste par poste, sans engagement.",
  },
  {
    q: "Travaillez-vous avec vos propres prestataires ?",
    a: "Nous nous appuyons sur un réseau de partenaires spécialisés et sélectionnés — lieux, traiteurs, techniciens, artistes. Nous les choisissons et les coordonnons pour vous.",
  },
  {
    q: "Qui est présent le jour J ?",
    a: "Votre chef de projet dédié supervise l'installation, coordonne les équipes sur place et reste votre unique contact jusqu'à la fin de l'événement.",
  },
];

function KineticHero({ settings }) {
  const videoUrl = settings.hero_video_url || "";

  return (
    <section className="relative overflow-hidden bg-white pt-28 md:pt-32" data-testid="hero">
      <div className="mx-auto grid max-w-[1440px] items-center gap-8 px-5 pb-14 md:grid-cols-12 md:gap-6 md:px-10 md:pb-20">
        <div className="md:col-span-6 md:pr-6">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className={overline}>
            Agence événementielle d'entreprise
          </motion.span>
          <h1 className="mt-5 font-serif text-5xl font-medium leading-[0.98] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.accent ? <span className="italic text-lime-dim">{line.text}</span> : line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-7 max-w-md text-lg leading-relaxed text-ink/60">
            Lieu, traiteur, bar mobile, personnel, technique, animation, sécurité et logistique : un chef de projet coordonne chaque prestation.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }} className="mt-9 flex flex-wrap gap-3">
            <Link to="/devis" onClick={() => track("click_brief")} className={btnPrimary} data-testid="hero-devis-btn">Commencer mon brief — 2 minutes <ArrowRight size={16} /></Link>
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={btnOutline} data-testid="hero-callback-btn">Parler à un chef de projet</a>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-5 flex items-center gap-2 text-sm text-ink/55" data-testid="hero-commitment">
            <Clock size={15} className="text-lime-dim" /> {settings.commitment}
          </motion.p>
        </div>

        <div className="md:col-span-6">
          <HeroSlideshow slides={HERO_SLIDES} videoUrl={videoUrl} interval={5500} />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [settings, setSettings] = useState({ commitment: "Première réponse sous 24 h ouvrées" });
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
    api.get("/projects").then((r) => setProjects((r.data || []).slice(0, 6))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
    api.get("/client-logos").then((r) => setClientLogos(r.data || [])).catch(() => {});
  }, []);

  const stats = [
    settings.stats_events && { value: settings.stats_events, label: "événements organisés" },
    settings.stats_years && { value: settings.stats_years, label: "ans d'expérience" },
    settings.stats_zone && { value: settings.stats_zone, label: "zone d'intervention" },
    { value: "24 h", label: "pour une première réponse" },
  ].filter(Boolean);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <>
      <Seo
        title="Agence événementielle à Lyon et Valence"
        description="Désir Events, agence événementielle d'entreprise à Lyon et Valence : inaugurations et soirées d'entreprise — traiteur, bar mobile, accueil et coordination réunis. Interlocuteur unique. Première réponse sous 24 h ouvrées."
        keywords="agence événementielle Lyon, agence événementielle Valence, inauguration entreprise Lyon, soirée d'entreprise Lyon, afterwork Lyon, bar mobile entreprise, organisation événement entreprise"
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <KineticHero settings={settings} />

      {/* Phrase SEO — positionnement national, ancre Lyon */}
      <section className="border-b border-ink/10 bg-mist" data-testid="home-seo-intro">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-10">
          <Reveal>
            <p className="mx-auto max-w-3xl text-center text-base leading-relaxed text-ink/60">
              Agence événementielle B2B basée à Lyon, Désir Events organise séminaires, soirées d'entreprise, inaugurations, lancements de produits, salons et conventions partout en France.{" "}
              <Link to="/agence-evenementielle-entreprise-lyon" className="font-medium text-ink underline decoration-lime-dim decoration-2 underline-offset-4 transition-colors hover:text-lime-dim">
                Découvrez notre agence événementielle à Lyon
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Noël — saison en cours : liens forts depuis l'accueil */}
      <section className="border-b border-ink/10 bg-ink text-white" data-testid="home-noel-band">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-6 px-5 py-8 md:px-10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.2em] text-lime">Noël d'entreprise</span>
            <p className="mt-2 font-serif text-xl text-white md:text-2xl">Soirée de Noël, arbre de Noël CSE, animations, décoration de magasin ou de bureaux.</p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {[
              { label: "Soirée de Noël Lyon", to: "/fete-de-noel-entreprise/lyon" },
              { label: "Arbre de Noël CSE", to: "/evenements-cse/lyon" },
              { label: "Animations Noël", to: "/animation-noel-entreprise" },
              { label: "Décoration magasin", to: "/decoration-noel-magasin" },
              { label: "Décoration entreprise", to: "/decoration-noel-entreprise" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="rounded-full border border-white/20 px-4 py-2 text-xs text-white/75 transition-colors hover:border-lime hover:text-lime" data-testid={`home-noel-${l.to.replace(/\//g, "")}`}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-ink/10 bg-lime py-5 text-ink" data-testid="editorial-marquee">
        <Marquee items={["Séminaires", "Soirées d'entreprise", "Inaugurations", "Lancements de produit", "Conventions", "Team building", "Salons professionnels", "Afterworks"]} className="font-serif text-4xl italic md:text-6xl" />
      </div>

      {/* PREUVES — chiffres réels saisis dans l'admin */}
      <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-10" data-testid="preuves-section">
        <Reveal><span className={overline}>Pourquoi nous faire confiance</span></Reveal>
        <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-4">
          {stats.map((st, i) => (
            <Reveal key={st.label} delay={i}>
              <div className="border-l-2 border-lime pl-6">
                <div className="font-serif text-5xl font-medium text-ink md:text-6xl">{st.value}</div>
                <div className="mt-2 text-sm text-ink/55">{st.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NOS ÉVÉNEMENTS */}
      <section className="border-t border-ink/10 bg-white" data-testid="evenements-section">
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Reveal><span className={overline}>Nos événements</span></Reveal>
              <Reveal delay={1}>
                <h2 className="mt-4 font-serif text-4xl font-medium leading-none tracking-tight text-ink md:text-6xl">Chaque format,<br />la même exigence</h2>
              </Reveal>
            </div>
            <Reveal delay={2}>
              <Link to="/prestations" className="flex items-center gap-2 text-sm text-ink/70 hover:text-ink link-underline">Toutes les prestations <ArrowUpRight size={16} /></Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i % 3}>
                <Link to={`/prestations/${s.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-ink/10 bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-ink/5" data-testid={`service-card-${s.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <span className="font-serif text-xl text-ink/25">0{i + 1}</span>
                    <h3 className="mt-3 font-serif text-2xl font-medium text-ink group-hover:text-lime-dim">{s.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/55">{s.short}</p>
                    <span className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-ink/50 group-hover:text-lime-dim">
                      Découvrir <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOS 3 OFFRES PRIORITAIRES */}
      <section className="border-t border-ink/10 bg-white" data-testid="offres-section">
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
          <Reveal><span className={overline}>Nos offres signature</span></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-6xl">Trois formats prêts<br />pour vos équipes</h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {OFFERS.map((o, i) => (
              <Reveal key={o.title} delay={i}>
                <div className="flex h-full flex-col rounded-2xl border border-ink/10 bg-mist p-8 transition-all duration-500 hover:-translate-y-1 hover:border-lime-dim/60" data-testid={`offer-${i}`}>
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-2xl leading-tight text-ink">{o.title}</h3>
                    <span className="shrink-0 rounded-full bg-lime px-3.5 py-1.5 text-xs font-semibold text-ink">{o.price}</span>
                  </div>
                  <p className="mt-2 text-xs uppercase tracking-[0.15em] text-ink/45">{o.capacity}</p>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {o.includes.map((inc) => (
                      <li key={inc} className="flex items-start gap-2 text-sm text-ink/70">
                        <Check size={14} className="mt-0.5 shrink-0 text-lime-dim" />{inc}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 text-xs leading-relaxed text-ink/50">{o.options}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-ink/50">{o.conditions}</p>
                  <Link to={o.to} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-lime-dim" data-testid={`offer-cta-${i}`}>
                    {o.cta} <ArrowRight size={15} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={2}>
            <p className="mt-6 text-xs text-ink/45">Fourchettes « à partir de » indicatives, HT. Chaque devis intègre achats, personnel, déplacements, installation, démontage et coordination — détaillé poste par poste.</p>
          </Reveal>
        </div>
      </section>

      {/* NOS RÉALISATIONS */}
      <section className="border-t border-ink/10 bg-mist" data-testid="home-realisations">
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Reveal><span className={overline}>Nos réalisations</span></Reveal>
              <Reveal delay={1}>
                <h2 className="mt-4 font-serif text-4xl font-medium leading-none tracking-tight text-ink md:text-6xl">Des événements<br />déjà livrés</h2>
              </Reveal>
            </div>
            <Reveal delay={2}>
              <Link to="/nos-realisations" className="flex items-center gap-2 text-sm text-ink/70 hover:text-ink link-underline">Toutes nos réalisations <ArrowUpRight size={16} /></Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={i % 3}>
                <Link to="/nos-realisations" className="group block overflow-hidden rounded-2xl border border-ink/10 bg-white" data-testid={`home-project-${p.id}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-lime-dim">{p.category}</span>
                    <h3 className="mt-2 font-serif text-xl font-medium text-ink">{p.title}</h3>
                    <p className="mt-1 text-xs text-ink/50">{[p.city, p.guests].filter(Boolean).join(" · ")}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTRE DIFFÉRENCE */}
      <section className="border-t border-ink/10 bg-white" data-testid="difference-section">
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
          <Reveal><span className={overline}>Notre différence</span></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-4 max-w-4xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-6xl">
              Pourquoi les entreprises nous confient <span className="italic text-lime-dim">leurs événements</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {DIFFERENCE_CARDS.map((c, i) => (
              <Reveal key={c.num} delay={i}>
                <div className="flex h-full flex-col rounded-2xl border border-ink/10 bg-mist p-8 transition-colors duration-500 hover:border-lime-dim/60" data-testid={`difference-${i}`}>
                  <span className="font-serif text-5xl text-lime-dim">{c.num}</span>
                  <h3 className="mt-6 font-serif text-2xl text-ink">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {(settings.manager_name || settings.insurance_text) && (
            <Reveal delay={1}>
              <div className="mt-8 flex flex-wrap gap-4" data-testid="reassurance-blocks">
                {settings.manager_name && (
                  <div className="rounded-2xl border border-ink/10 bg-white px-7 py-5">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Votre interlocuteur</p>
                    <p className="mt-1 font-serif text-xl text-ink">{settings.manager_name}{settings.manager_role ? <span className="text-ink/50"> — {settings.manager_role}</span> : ""}</p>
                  </div>
                )}
                {settings.insurance_text && (
                  <div className="rounded-2xl border border-ink/10 bg-white px-7 py-5">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Garanties</p>
                    <p className="mt-1 font-serif text-xl text-ink">{settings.insurance_text}</p>
                  </div>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="border-t border-ink/10 bg-ink text-white" data-testid="methode-section">
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
          <Reveal><span className={`${overline} !text-lime`}>Comment ça marche</span></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-none tracking-tight md:text-6xl">De votre brief<br />au jour J</h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl bg-white/10 md:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i}>
                <div className="flex h-full flex-col bg-ink p-8" data-testid={`step-${i}`}>
                  <span className="font-serif text-4xl text-lime">{s.num}</span>
                  <h3 className="mt-5 font-serif text-xl">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES COMPLÉMENTAIRES — réseau de partenaires */}
      <section className="border-t border-ink/10 bg-white" data-testid="partners-section">
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <Reveal><span className={overline}>Services complémentaires</span></Reveal>
              <Reveal delay={1}>
                <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-6xl">
                  Traiteur, bar mobile, personnel, technique : <span className="italic text-lime-dim">tout sous la même main</span>
                </h2>
              </Reveal>
              <Reveal delay={2}>
                <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink/60">
                  Désir Events sélectionne et coordonne les partenaires adaptés à chaque événement selon votre concept, votre budget et vos exigences.
                </p>
              </Reveal>
              <Reveal delay={3}>
                <p className="mt-8 font-serif text-2xl italic text-ink">Vous gardez un seul interlocuteur.<br />Nous coordonnons l'ensemble.</p>
              </Reveal>
            </div>

            <div className="md:col-span-7">
              <Reveal>
                <div className="flex flex-wrap gap-2" data-testid="partners-pills">
                  {[
                    "Organisation",
                    "Coordination",
                    "Traiteur",
                    "Bar mobile",
                    "Personnel",
                    "Hôtesses",
                    "Animation",
                    "Mobilier",
                    "Technique",
                    "Logistique",
                  ].map((p) => (
                    <span key={p} className="rounded-full border border-ink/15 bg-mist px-4 py-2 text-sm text-ink/70">{p}</span>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={1}>
                <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {PARTNERS.map((p, i) => (
                    <a
                      key={p.domain}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1 ${i === 0 || i === 3 ? "bg-lime hover:shadow-2xl hover:shadow-lime-dim/20" : i === 1 || i === 4 ? "bg-ink text-white hover:shadow-2xl hover:shadow-ink/30" : "border border-ink/10 bg-mist hover:border-lime-dim/60"}`}
                      data-testid={`partner-${p.domain.replace(/\./g, "-")}`}
                    >
                      <div>
                        <span className={`text-[11px] uppercase tracking-[0.2em] ${i === 1 || i === 4 ? "text-white/60" : "text-ink/50"}`}>{p.category}</span>
                        <h3 className={`mt-3 font-serif text-2xl font-medium leading-tight ${i === 1 || i === 4 ? "text-white" : "text-ink"}`}>{p.name}</h3>
                        <p className={`mt-3 text-sm leading-relaxed ${i === 1 || i === 4 ? "text-white/70" : "text-ink/60"}`}>{p.role}</p>
                      </div>
                      <span className={`mt-6 flex items-center gap-2 text-sm font-medium ${i === 1 || i === 4 ? "text-lime" : "text-ink"}`}>
                        {p.domain}
                        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </span>
                    </a>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* LOGOS CLIENTS — réels et autorisés, saisis dans l'admin, masqués si aucun */}
      {clientLogos.length > 0 && (
        <section className="border-t border-ink/10 bg-white" data-testid="client-logos-section">
          <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-10">
            <Reveal><span className={overline}>Ils nous ont fait confiance</span></Reveal>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-14 gap-y-8">
              {clientLogos.map((l) => (
                <img key={l.id} src={l.image} alt={l.name} title={l.name} loading="lazy"
                  className="h-10 w-auto max-w-[160px] object-contain grayscale opacity-70 transition-all duration-300 hover:grayscale-0 hover:opacity-100"
                  data-testid={`client-logo-${l.id}`} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AVIS — réels, saisis dans l'admin, masqués si aucun */}
      {testimonials.length > 0 && (
        <section className="border-t border-ink/10 bg-mist" data-testid="testimonials-section">
          <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
            <Reveal><span className={overline}>Ce qu'ils disent de nous</span></Reveal>
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i % 3}>
                  <figure className="flex h-full flex-col justify-between rounded-2xl border border-ink/10 bg-white p-8" data-testid={`testimonial-${t.id}`}>
                    <blockquote className="font-serif text-2xl leading-snug text-ink">"{t.text}"</blockquote>
                    <figcaption className="mt-8">
                      <div className="text-sm font-semibold text-ink">{t.author}</div>
                      {t.role && <div className="text-xs text-ink/50">{t.role}</div>}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-white" data-testid="faq-section">
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <Reveal><span className={overline}>Questions fréquentes</span></Reveal>
              <Reveal delay={1}>
                <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">Ce que les entreprises<br />nous demandent</h2>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <div className="grid gap-4 md:grid-cols-2">
                {FAQ.map((f, i) => (
                  <Reveal key={f.q} delay={i % 2}>
                    <div className="h-full rounded-2xl border border-ink/10 bg-mist p-7" data-testid={`faq-${i}`}>
                      <h3 className="font-serif text-xl leading-snug text-ink">{f.q}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-ink/60">{f.a}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAILLAGE INTERNE — expertises locales prioritaires */}
      <section className="border-t border-ink/10 bg-mist" data-testid="local-links-section">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
          <Reveal><span className={overline}>Nos expertises par ville</span></Reveal>
          <Reveal delay={1}>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {[
                { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle Lyon" },
                { to: "/seminaire-convention-lyon", label: "Séminaire Lyon" },
                { to: "/soiree-entreprise-lyon", label: "Soirée d'entreprise Lyon" },
                { to: "/inauguration-entreprise-lyon", label: "Inauguration Lyon" },
                { to: "/lancement-produit-lyon", label: "Lancement de produit Lyon" },
                { to: "/salon-professionnel-lyon", label: "Salon professionnel Lyon" },
                { to: "/agence-evenementielle-clermont-ferrand", label: "Agence Clermont-Ferrand" },
                { to: "/agence-evenementielle-grenoble", label: "Agence Grenoble" },
                { to: "/agence-evenementielle-valence", label: "Agence Valence" },
                { to: "/soiree-entreprise-paris", label: "Soirée d'entreprise Paris" },
                { to: "/lancement-produit-paris", label: "Lancement de produit Paris" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink">
                  {l.label}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="mx-auto max-w-[1440px] px-5 py-28 md:px-10 md:py-40" data-testid="cta-section">
        <Reveal>
          <h2 className="max-w-4xl font-serif text-5xl font-medium leading-[0.98] tracking-tight text-ink md:text-8xl">
            Recevez une<br /><span className="italic text-lime-dim">première proposition</span>.
          </h2>
        </Reveal>
        <Reveal delay={1}>
          <p className="mt-6 max-w-xl text-lg text-ink/60">{settings.commitment}. Proposition détaillée et chiffrée, sans engagement.</p>
        </Reveal>
        <Reveal delay={2}>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary} data-testid="cta-devis-btn">Déposer mon brief <ArrowRight size={16} /></Link>
            <Link to="/nos-realisations" className={btnOutline}>Voir nos réalisations</Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
